import { OddsType } from "app/utils/const";
import { ChartDataset } from "chart.js/auto";
import get from "lodash/get";

const chartColours = Object.freeze({
  bar: "#e2e9e9",
  line1: "#cb4a48",
  line2: "#737373",
  line3: "#4970ab",
  barTooltip: "#99b2b2",
  pointHoverBorderColor: "#4da1a9",
  pointHoverBackgroundColor: "#ffffff",
  tooltipTextColor: "#ffffff",
});

/**
 *
 * @param pricePoints
 * @returns an array of numbers
 */
const getDataForOdds = pricePoints => {
  return pricePoints.map(pricePoint => pricePoint.unroundedDecimalOdds);
};

export class ChartHelpers {
  /**
   *
   * @param props
   * @returns configurations for the scales
   */
  static getScales(props): any {
    return {
      xAxis: {
        //horizontal scale on bottom showing time
        offset: true,
        type: "time",
        position: "bottom",
        axis: "x",
        time: {
          unit: "hour",
          stepSize: 1,
          displayFormats: {
            hour: "HH:mm",
          },
        },
        ticks: {
          autoSkip: true,
          maxTicksLimit: 5,
          maxRotation: 0,
          minRotation: 0,
        },
        grid: {
          display: false,
          drawBorder: false,
        },
        title: {
          display: true,
          text: props.intl.formatMessage({
            id: "charts-last-12-hours",
          }),
        },
      },
      A: {
        //vertical scale on left of graph showing odds
        id: "A",
        type: "linear",
        position: "left",
        axis: "y",
        beginAtZero: false,
        ticks: {
          callback: (label, index, labels) => {
            //chartjs genertates scale values for the odds axis, depending on values to be plotted
            if (props.selectedOddsType === OddsType.US) {
              // decimal odds values are used to draw the graph but we need to show axis labels in US odds
              // convert decimal odds to US odds
              if (label === 1) {
                // no equivalent US odds for decimal 1
                return "";
              }

              if (label >= 2) {
                return `+${Math.floor((label - 1) * 100)}`;
              } else {
                return Math.floor((100 / (label - 1)) * -1);
              }
            } else {
              return props.intl.formatNumber(label, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              });
            }
          },
        },
        grid: {
          display: false,
          drawBorder: false,
        },
      },
      B: {
        //vertical scale on right of graph showing currency
        id: "B",
        type: "linear",
        position: "right",
        axis: "y",
        beginAtZero: false,
        ticks: {
          callback: (value, index, values) => {
            return props.intl.formatNumber(value, {
              style: "currency",
              currency: props.currencyCode,
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            });
          },
        },
        grid: {
          display: false,
          drawBorder: false,
        },
      },
    };
  }
  /**
   *
   * @param context
   * @returns Returns a string: "odds" or "stake" depending on which data is hovered
   */
  static getTooltipText(context) {
    const { formatNumber, formatMessage } = context.chart.props.intl;

    if (context.dataset.yAxisID === "A") {
      //means odds data point
      // context.chart.config._config.options.plugins.tooltip.backgroundColor =
      //   chartColours.line1;
      const odds = get(context, [
        "chart",
        "props",
        "runnerData",
        context.datasetIndex,
        "pricePoints",
        context.dataIndex,
        context.chart.props.selectedOddsType === OddsType.US
          ? "usOdds"
          : "decimalOdds",
      ]);
      return odds
        ? odds > 0
          ? `${formatMessage({ id: "bets-odds" })}: +${odds}`
          : `${formatMessage({ id: "bets-odds" })}: ${odds}`
        : "";
    } else if (context.dataset.yAxisID === "B") {
      // stake
      // context.chart.config._config.options.plugins.tooltip.backgroundColor =
      //   chartColours.line2;
      const liquidity = context.raw;
      return isNaN(liquidity)
        ? ""
        : `${formatMessage({ id: "bets-maxbet" })}: ${formatNumber(liquidity, {
            style: "currency",
            currency: context.chart.props.currencyCode,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })}`;
    } else {
      return "";
    }
  }

  /**
   *
   * @param context
   * @returns a colour
   * For changing  colour of tooltips depending on the dataset
   */
  static getLabelColor(context) {
    if (context.datasetIndex === 0) {
      return {
        backgroundColor: chartColours.line1,
        borderColor: chartColours.line1,
      };
    } else if (context.datasetIndex === 1) {
      return {
        backgroundColor: chartColours.line3,
        borderColor: chartColours.line3,
      };
    } else if (context.datasetIndex === 2) {
      return {
        backgroundColor: chartColours.barTooltip,
        borderColor: chartColours.barTooltip,
      };
    } else {
      return {
        backgroundColor: chartColours.barTooltip,
        borderColor: chartColours.barTooltip,
      };
    }
  }

  /**
   *
   * @param context
   * @returns a string with runnner name or empty string
   */
  static getToolTipTitle(context): string {
    // show runner name when hovering on odds
    if (context && context.length > 0 && context[0]) {
      const yAxisID = get(context[0], ["dataset", "yAxisID"]);
      if (yAxisID === "A") {
        // odds data point so find runner name from the data
        const runnerData: {} = get(context[0], [
          "chart",
          "props",
          "runnerData",
        ]);
        const runnerName: string = get(runnerData[context[0].datasetIndex], [
          "runner",
          "name",
        ]);
        return runnerName;
      } else {
        // don't show anything when hovering on stake
        return "";
      }
    } else {
      // nothing to show
      return "";
    }
  }
  /**
   *
   * @param context
   * @returns a colour
   */
  static getLabelTextColor(context) {
    return chartColours.tooltipTextColor;
  }

  /**
   *
   * @param props
   * @returns an array of datasets needed to draw the charts
   * One Odds dataset for each runner and a stake dataset
   */
  static getDatasets(props): ChartDataset[] {
    // markets to be displayed should have 2 runners
    if (props.runnerData && props.runnerData.length === 2) {
      return [
        {
          label: "Odds",
          yAxisID: "A",
          type: "line",
          data: getDataForOdds(props.runnerData[0].pricePoints),
          fill: false,
          backgroundColor: chartColours.line1,
          borderColor: chartColours.line1,
          pointHoverBackgroundColor: chartColours.pointHoverBackgroundColor,
          pointHoverBorderColor: chartColours.pointHoverBorderColor,
          // lineTension: 0,
          borderWidth: 2,
          spanGaps: false,
        },
        {
          label: "Odds",
          yAxisID: "A",
          type: "line",
          data:
            props.runnerData[1] !== undefined &&
            getDataForOdds(props.runnerData[1].pricePoints),
          fill: false,
          backgroundColor: chartColours.line3,
          borderColor: chartColours.line3,
          pointHoverBackgroundColor: chartColours.pointHoverBackgroundColor,
          pointHoverBorderColor: chartColours.pointHoverBorderColor,
          // lineTension: 0,
          borderWidth: 2,
          spanGaps: false,
        },
        {
          label: "Stake",
          yAxisID: "B",
          type: "bar",
          data: props.liquidityOfBestRunner,
          // fill: false,
          backgroundColor: chartColours.bar,
          // pointHitRadius: 0,
        },
      ];
    } else {
      return [];
    }
  }
}
